import type { NativeCatalogFloor } from '@/api/core/native-property-read';
import { SearchField } from '@/components/search-field';
import { isValidSearchText, MAX_SEARCH_TEXT_LENGTH } from '@/lib/search-text';
import {
  Alert,
  AlertDescription,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mgrvtechgroup/admin-ds';
import { useState } from 'react';

export interface SalesOrder {
  sortBy: 'floor' | 'name';
  sortDirection: 'asc' | 'desc';
  /** 정확한 층. `floorIsNull`과 동시에 쓰지 않는다(서버가 422). */
  floor: number | null;
  /** 층 미지정(NULL) Room만. */
  floorIsNull: boolean;
  /** 서버에 보내는 확정 검색어(앞뒤 공백 제거, 비면 필터 없음). */
  q: string;
}
export const DEFAULT_SALES_ORDER: SalesOrder = {
  sortBy: 'floor',
  sortDirection: 'asc',
  floor: null,
  floorIsNull: false,
  q: '',
};

const ALL_FLOORS = 'all';
const NULL_FLOOR = 'null';
export const floorToken = (order: Pick<SalesOrder, 'floor' | 'floorIsNull'>) =>
  order.floorIsNull ? NULL_FLOOR : order.floor === null ? ALL_FLOORS : String(order.floor);
export const floorLabel = (floor: number | null) =>
  floor === null ? '층 미지정' : `${String(floor)}층`;

export const salesOrderLabel = (order: SalesOrder, nameLabel: string) =>
  `${order.sortBy === 'floor' ? '층' : nameLabel} ${order.sortDirection === 'asc' ? '오름차순' : '내림차순'}${
    order.floorIsNull ? ' · 층 미지정' : order.floor === null ? '' : ` · ${String(order.floor)}층`
  }${order.q ? ` · “${order.q}” 검색` : ''}`;

export interface FloorFacet {
  data?: NativeCatalogFloor[];
  isError: boolean;
  isFetching: boolean;
  retry: () => void;
}

/** 필터가 하나라도 걸렸는지(빈 결과 안내 문구를 나누는 데 쓴다). */
export const hasSalesFilter = (order: SalesOrder) =>
  order.q !== '' || order.floor !== null || order.floorIsNull;

export function emptyFilteredTitle(order: SalesOrder, subject: string, base: string) {
  if (order.q !== '')
    return `“${order.q}”에 맞는 ${subject}이 없습니다. 검색어를 지우거나 바꿔 보세요.`;
  if (order.floorIsNull) return `층이 지정되지 않은 ${subject}이 없습니다.`;
  if (order.floor !== null)
    return `${String(order.floor)}층에 해당하는 ${subject}이 없습니다. 층 필터를 해제하면 전체를 봅니다.`;
  return base;
}

/**
 * 서버가 받는 검색어(q)·정렬(floor|name)·방향·층(facet)만 고른다. 서버가 거른 결과만 보이며
 * 이 컨트롤은 클라이언트에서 다시 거르거나 정렬하지 않는다. 값이 바뀌면 호출자가 cursor를 버려야 하므로
 * 변경은 모두 onChange 한 곳으로 올린다.
 */
export function SalesOrderControls({
  subject,
  nameLabel,
  searchPlaceholder,
  order,
  floors,
  onChange,
}: {
  /** 접근성 이름에 붙는 대상(예: 객실, 슬롯). */
  subject: string;
  /** 'name' 정렬의 표시 이름(예: 호실 순). */
  nameLabel: string;
  /** 서버가 매칭하는 범위(예: 객실명, 객실·슬롯명). */
  searchPlaceholder: string;
  order: SalesOrder;
  floors: FloorFacet;
  onChange: (next: SalesOrder) => void;
}) {
  const [searchRejected, setSearchRejected] = useState(false);
  const current = floorToken(order);
  const known = new Set(
    (floors.data ?? []).map((row) =>
      floorToken({ floor: row.floor, floorIsNull: row.floor === null }),
    ),
  );
  const options = [
    { value: ALL_FLOORS, label: '전체 층' },
    ...(floors.data ?? []).map((row) => ({
      value: row.floor === null ? NULL_FLOOR : String(row.floor),
      label: `${floorLabel(row.floor)} (객실 ${String(row.room_count)})`,
    })),
    // 적용 중인 층이 선택지에 없어도(조회 전·실패·방금 사라진 층) 선택 상태가 비지 않게 한다.
    ...(current !== ALL_FLOORS && !known.has(current)
      ? [{ value: current, label: floorLabel(order.floorIsNull ? null : order.floor) }]
      : []),
  ];
  return (
    <>
      <div className="flex flex-wrap items-end gap-3">
        <div className="grid gap-1">
          <span className="caption">검색</span>
          <SearchField
            value={order.q}
            placeholder={searchPlaceholder}
            aria-label={`${subject} 검색`}
            onSearch={(next) => {
              // 서버가 거부할 검색어(정규화 뒤 100자 초과)는 보내지 않고 이유를 알린다. 지우기는 항상 허용한다.
              const valid = next === '' || isValidSearchText(next);
              setSearchRejected(!valid);
              if (!valid || next === order.q) return;
              onChange({ ...order, q: next });
            }}
          />
        </div>
        <div className="grid gap-1">
          <span className="caption">정렬 기준</span>
          <Select
            value={order.sortBy}
            onValueChange={(value) => {
              if (value === 'floor' || value === 'name') onChange({ ...order, sortBy: value });
            }}
            items={[
              { value: 'floor', label: '층 순' },
              { value: 'name', label: nameLabel },
            ]}
          >
            <SelectTrigger aria-label={`${subject} 정렬 기준`} className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="floor">층 순</SelectItem>
              <SelectItem value="name">{nameLabel}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1">
          <span className="caption">정렬 방향</span>
          <Select
            value={order.sortDirection}
            onValueChange={(value) => {
              if (value === 'asc' || value === 'desc') onChange({ ...order, sortDirection: value });
            }}
            items={[
              { value: 'asc', label: '오름차순' },
              { value: 'desc', label: '내림차순' },
            ]}
          >
            <SelectTrigger aria-label={`${subject} 정렬 방향`} className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="asc">오름차순</SelectItem>
              <SelectItem value="desc">내림차순</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1">
          <span className="caption">층</span>
          <Select
            value={current}
            onValueChange={(value) => {
              if (value === null) return;
              if (value === ALL_FLOORS) onChange({ ...order, floor: null, floorIsNull: false });
              else if (value === NULL_FLOOR) onChange({ ...order, floor: null, floorIsNull: true });
              else onChange({ ...order, floor: Number(value), floorIsNull: false });
            }}
            items={options}
          >
            <SelectTrigger aria-label={`${subject} 층 필터`} className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {searchRejected && (
        <p role="alert" className="caption">
          검색어는 {String(MAX_SEARCH_TEXT_LENGTH)}자 이하로 입력하세요.
        </p>
      )}
      {floors.isError && (
        <Alert variant="warning" role="status" aria-live="polite">
          <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
            <span>층 선택지를 불러오지 못했습니다. 목록은 그대로 사용할 수 있습니다.</span>
            <Button variant="outline" disabled={floors.isFetching} onClick={floors.retry}>
              {floors.isFetching ? '조회 중…' : '다시 조회'}
            </Button>
          </AlertDescription>
        </Alert>
      )}
    </>
  );
}
